import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { LlmProviderUpdateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { InputStringList } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type LlmProviderUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  name: string;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  nameError?: string;
  code: string;
  codeSelectedValue?: { displayId: string; id: string } | null;
  onCodeChange?: (value: string) => void;
  codeError?: string;
  requiresApiKey: boolean;
  requiresApiKeySelectedValue?: { displayId: string; id: string } | null;
  onRequiresApiKeyChange?: (value: boolean) => void;
  requiresApiKeyError?: string;
  requiresBaseUrl: boolean;
  requiresBaseUrlSelectedValue?: { displayId: string; id: string } | null;
  onRequiresBaseUrlChange?: (value: boolean) => void;
  requiresBaseUrlError?: string;
  defaultModels: string[];
  defaultModelsSelectedValue?: { displayId: string; id: string } | null;
  onDefaultModelsChange?: (value: string[]) => void;
  defaultModelsError?: string;
  logoUrl: string;
  logoUrlSelectedValue?: { displayId: string; id: string } | null;
  onLogoUrlChange?: (value: string) => void;
  logoUrlError?: string;
  llmProviderUpdateStore: LlmProviderUpdateStore;
};

export const LlmProviderUpdateForm: React.FC<LlmProviderUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name = "",
      nameError,
      onNameChange = () => {},
      code = "",
      codeError,
      onCodeChange = () => {},
      requiresApiKey = false,
      requiresApiKeyError,
      onRequiresApiKeyChange = () => {},
      requiresBaseUrl = false,
      requiresBaseUrlError,
      onRequiresBaseUrlChange = () => {},
      defaultModels = [],
      defaultModelsError,
      onDefaultModelsChange = () => {},
      logoUrl = "",
      logoUrlError,
      onLogoUrlChange = () => {},
      llmProviderUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="LlmProvider"
              mode="update"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "code":
                    onCodeChange?.(valor);
                    break;
                  case "requiresApiKey":
                    onRequiresApiKeyChange?.(valor);
                    break;
                  case "requiresBaseUrl":
                    onRequiresBaseUrlChange?.(valor);
                    break;
                  case "defaultModels":
                    onDefaultModelsChange?.(valor);
                    break;
                  case "logoUrl":
                    onLogoUrlChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={llmProviderUpdateStore.getCardErrors("null")}
              title={langStore.groupLlmProviderLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name}
                  error={Boolean(nameError)}
                  warning={
                    !nameError &&
                    Boolean(llmProviderUpdateStore.warnings["name"])
                  }
                  helperText={
                    nameError ||
                    llmProviderUpdateStore.warnings["name"]?.message
                  }
                  label={langStore.LlmProviderNameLabel}
                  onChange={(value: string) => onNameChange(value)}
                  description={langStore.LlmProviderNameDescription}
                />
                <InputString
                  value={code}
                  error={Boolean(codeError)}
                  warning={
                    !codeError &&
                    Boolean(llmProviderUpdateStore.warnings["code"])
                  }
                  helperText={
                    codeError ||
                    llmProviderUpdateStore.warnings["code"]?.message
                  }
                  label={langStore.LlmProviderCodeLabel}
                  onChange={(value: string) => onCodeChange(value)}
                  description={langStore.LlmProviderCodeDescription}
                />
                <InputBoolean
                  value={requiresApiKey}
                  error={Boolean(requiresApiKeyError)}
                  helperText={
                    requiresApiKeyError ||
                    llmProviderUpdateStore.warnings["requiresApiKey"]?.message
                  }
                  label={langStore.LlmProviderRequiresApiKeyLabel}
                  description={langStore.LlmProviderRequiresApiKeyDescription}
                  onChange={(value: boolean) => onRequiresApiKeyChange(value)}
                />
                <InputBoolean
                  value={requiresBaseUrl}
                  error={Boolean(requiresBaseUrlError)}
                  helperText={
                    requiresBaseUrlError ||
                    llmProviderUpdateStore.warnings["requiresBaseUrl"]?.message
                  }
                  label={langStore.LlmProviderRequiresBaseUrlLabel}
                  description={langStore.LlmProviderRequiresBaseUrlDescription}
                  onChange={(value: boolean) => onRequiresBaseUrlChange(value)}
                />
                <InputStringList
                  value={defaultModels}
                  error={Boolean(defaultModelsError)}
                  helperText={
                    defaultModelsError ||
                    llmProviderUpdateStore.warnings["defaultModels"]?.message
                  }
                  label={langStore.LlmProviderDefaultModelsLabel}
                  description={langStore.LlmProviderDefaultModelsDescription}
                  onChange={(value: string[]) => onDefaultModelsChange(value)}
                />
                <InputString
                  value={logoUrl}
                  error={Boolean(logoUrlError)}
                  warning={
                    !logoUrlError &&
                    Boolean(llmProviderUpdateStore.warnings["logoUrl"])
                  }
                  helperText={
                    logoUrlError ||
                    llmProviderUpdateStore.warnings["logoUrl"]?.message
                  }
                  label={langStore.LlmProviderLogoUrlLabel}
                  onChange={(value: string) => onLogoUrlChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.LlmProviderLogoUrlDescription}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
